import { ChevronRight } from 'lucide-react';

// The buyer-facing secondary-action pattern: a neutral dark card with a small
// icon chip whose RING carries the action's colour, label under (tile) or beside
// it (row). Colour never fills the tile — that weight is reserved for the two
// primary conversion actions in the pinned dock.
//
// Grid columns are minmax(0,1fr), not 1fr: `1fr` is min-content-floored, so a
// long label (or a wide logo) pushes the tile past the column and the row
// overflows the screen.
export const ACTION_GRID = 'grid gap-2.5 [grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]';

const CARD = 'bg-[#161616] border-[#2a2a2a]';
const CARD_LIGHT = 'bg-white border-black/10';
// Hairline borders set inline: Tailwind's arbitrary border widths get rounded
// to 1px by the compiled utility, which loses the hairline look.
const CARD_BORDER = { borderWidth: '0.5px', borderStyle: 'solid' };
const CHIP_BORDER = { borderWidth: '1.5px', borderStyle: 'solid' };

const chipCls = 'w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 bg-transparent';

/** Icon chip — transparent fill, 1.5px coloured ring, icon in the same colour. */
export const ActionChip = ({ color, children, className = '' }) => (
    <span className={`${chipCls} ${className}`} style={{ ...CHIP_BORDER, borderColor: color, color }}>
        {children}
    </span>
);

/** One tile in the grid. Equal-weight actions only. `sublabel` is for a brand
 *  mark under the label (CARFAX) — the chip stays top-aligned either way. */
export const ActionTile = ({ color, icon: Icon, label, sublabel, onClick, testid, light, children }) => (
    <button type="button" onClick={onClick} data-testid={testid} data-tactile="select" style={CARD_BORDER}
        className={`w-full min-w-0 rounded-[15px] px-3 py-4 flex flex-col items-center justify-start gap-2 text-center transition-transform active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${light ? CARD_LIGHT : CARD}`}>
        <ActionChip color={color}>
            {Icon ? <Icon className="w-[17px] h-[17px]" strokeWidth={1.75} /> : children}
        </ActionChip>
        <span className={`text-[13px] font-semibold leading-tight min-w-0 break-words ${light ? 'text-neutral-900' : 'text-neutral-100'}`}>
            {label}
        </span>
        {sublabel}
    </button>
);

/** Full-width row for a single lower-frequency action — chip, label, chevron. */
export const ActionRow = ({ color, icon: Icon, label, sublabel, onClick, testid, light, children }) => (
    <button type="button" onClick={onClick} data-testid={testid} data-tactile="select" style={CARD_BORDER}
        className={`w-full rounded-[15px] px-3.5 py-3 flex items-center gap-3 text-left transition-transform active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100 ${light ? CARD_LIGHT : CARD}`}>
        <ActionChip color={color}>
            {Icon ? <Icon className="w-[17px] h-[17px]" strokeWidth={1.75} /> : children}
        </ActionChip>
        <span className="flex-1 min-w-0">
            <span className={`block text-[14px] font-semibold leading-tight ${light ? 'text-neutral-900' : 'text-neutral-100'}`}>{label}</span>
            {sublabel ? <span className="block text-[11px] mt-0.5 text-neutral-400 leading-snug">{sublabel}</span> : null}
        </span>
        <ChevronRight className={`w-[18px] h-[18px] shrink-0 ${light ? 'text-neutral-400' : 'text-neutral-500'}`} />
    </button>
);
